import { makeAutoObservable } from 'mobx';
import i18n from '../../../i18n';
import { DataAnalysis } from '../../../models';
import { Order } from '../../../models/order.entity';
import { Dish } from '../../../models/dish.entity';
import apiManage from '../../../request';
import {
    applyHistoryOrderDetailAmountFallback,
    cloneOrderDetailTableRowFallback,
    mergeOrderDetailSummaryWithTableRow,
    parseDataAnalysisOrderDetailWithHead,
    resolveOrderDetailCheckIdQuery,
} from '../../../request/api/data-analysis';
import { SortType, handleSort, validateCurrentDate } from '../../../constants/tool-order';
import { FetchCancelGate } from '../../../utils/fetch-cancel-gate';
import moment from 'moment';
import { exportExcel, formatEUR } from '../../../constants/tool.fn';
import {
    formatRefundCheckId,
    formatRefundOrderHeadId,
    getRefundDishReasonDisplayText,
    withCloudRefundHistoryFlag,
    withInstantRefundHistoryFlag,
    type RefundDishRow,
} from './refund-reason-display';
import GlobalNotification from '../../../component/common/snackbar';
import { MobxObjectsManager } from '../../mobx-objs/mobx-manager';
import MainPageState from '../../main/main-page-states';

class RefundDataStore {
    // loading
    loading: boolean = true;
    // 退菜详情
    data: DataAnalysis[] = []

    // 排序
    sortKeyValue: SortType = {}
    // 源数据
    dataSource: DataAnalysis[] = []
    // 是否成功获取了数据
    errorMessage: string = '';
    /** 列表请求取消门闩（日期快速切换时 cancel 上一请求） */
    private listFetchGate = new FetchCancelGate();
    /** 订单详情弹窗是否打开 */
    isOpenOrderDetail = false;
    /** 打开详情时仅保留 order_head_id 或 server_id */
    orderDetailData: Order = {};
    /** 详情摘要：默认列表行（或 stub），接口有值字段再覆盖 */
    orderDetailSummary: Order | null = null;
    /** 详情菜品列表 */
    orderDetailDish: Dish[] = [];
    /** 详情接口加载中 */
    detailLoading = false;

    constructor() {
        makeAutoObservable(this)
    }

    /**
     * 获取数据
     */
    async fetchData(stringId: string, startDate: string, endDate: string, pageType: PageType) {
        const { cancelToken, seq } = this.listFetchGate.begin();
        this.setLoading(true);
        try {
            let result;
            if (pageType === 'CLOUD') {
                result = await apiManage.get_data_analysis_history_cancelled_dish_period({
                    string_id: stringId,
                    start: startDate,
                    end: endDate,
                }, { cancelToken });
            } else {
                const { startTime, endTime } = validateCurrentDate();
                result = await apiManage.get_data_analysis_cancelled_dish({
                    string_id: stringId,
                    start: startTime,
                    end: endTime,
                }, { cancelToken });
            }
            if (!this.listFetchGate.isLatest(seq)) {
                return;
            }
            if (!result?.error) {
                /** 列表直接使用接口数据；按页面补全 `is_history` 缺省值以正确展示退菜原因类型。 */
                const list = Array.isArray(result)
                    ? result.map((row) =>
                        pageType === 'CLOUD'
                            ? withCloudRefundHistoryFlag(row)
                            : withInstantRefundHistoryFlag(row)
                    )
                    : [];
                this.setData(list);
                this.setErrorMessage('');
            } else {
                if (result.message !== 'canceled') {
                    if (result?.result?.statusCode === 404 && pageType === 'CLOUD') {
                        this.setErrorMessage(i18n.t('business_analysis_page_text_restaurant_data_not_synchronized'));
                    } else if (result?.result?.statusCode === 404 && pageType === 'INSTANT') {
                        this.setErrorMessage(i18n.t('business_analysis_page_msg_no_database_config'));
                    } else {
                        this.setErrorMessage(i18n.t('business_analysis_page_get_refund_data_failed'));
                    }
                    this.setData([]);
                }
            }
        } finally {
            this.listFetchGate.endLoading(seq, (value) => this.setLoading(value));
        }
    }

    setData(value: DataAnalysis[]) {
        this.data = value
    }

    /**
     * 拉取订单详情：隧道用 order_detail_with_head；云端用 history_order/head（id=server_id）。
     * @param pageType 退菜页模式
     */
    async fetchDetailData(pageType: PageType) {
        this.setDetailLoading(true);
        const mainPageData = MobxObjectsManager.get(MainPageState);
        const stringId = mainPageData.currentRestaurant?.string_id || '';
        const checkIdQuery = resolveOrderDetailCheckIdQuery(this.orderDetailData.check_id);
        try {
            const res =
                pageType === 'CLOUD'
                    ? await apiManage.get_data_analysis_res_history_order_details({
                          string_id: stringId,
                          id: `${this.orderDetailData.server_id || ''}`,
                      })
                    : await apiManage.get_data_analysis_order_details({
                          string_id: stringId,
                          order_head_id: `${this.orderDetailData.order_head_id || ''}`,
                          ...(checkIdQuery ? { check_id: checkIdQuery } : {}),
                          sort: 'ASC',
                      });
            const { order, details } = parseDataAnalysisOrderDetailWithHead(res);
            if (!res?.error) {
                const apiOrder =
                    pageType === 'CLOUD'
                        ? applyHistoryOrderDetailAmountFallback(order)
                        : order;
                this.setOrderDetailSummary(
                    mergeOrderDetailSummaryWithTableRow(this.orderDetailData, apiOrder)
                );
                this.setOrderDetailDish(details ?? []);
            } else {
                GlobalNotification.error(i18n.t('immediate_get_order_details_failed'));
            }
        } finally {
            this.setDetailLoading(false);
        }
    }

    /** 打开/关闭订单详情弹窗 */
    setOpenOrderDetail(value: boolean) {
        this.isOpenOrderDetail = value;
        if (!value) {
            this.orderDetailSummary = null;
            this.orderDetailDish = [];
            this.orderDetailData = {};
        }
    }

    /** 写入列表行请求标识；摘要先展示已有表格字段，接口返回后再合并 */
    setOrderDetailData(value: Order) {
        this.orderDetailData = value;
        this.orderDetailSummary = cloneOrderDetailTableRowFallback(value);
    }

    setOrderDetailSummary(value: Order | null) {
        this.orderDetailSummary = value;
    }

    setOrderDetailDish(value: Dish[]) {
        this.orderDetailDish = value;
    }

    setDetailLoading(value: boolean) {
        this.detailLoading = value;
    }

    /**
     * 更新loading
     * @param value
     */
    setLoading(value: boolean) {
        this.loading = value
    }

    /**
     * 排序
     * @param value
     */
    setSortValue(value: SortType) {
        this.sortKeyValue = value
        if (value.key && value.value) {
            this.data = handleSort(value, this.data, 'Number') || []
        } else {
            this.data = [...this.data || []]
        }
    }
    /**
     * 更新errorMessage
     */
    setErrorMessage(value: string) {
        this.errorMessage = value
    }

    /**
     * 云退菜详情：导出与当前表格列一致的 xlsx；即时退菜页无顶栏导出入口，此处仍兼容 `INSTANT`
     */
    downloadExcel(pageType: PageType) {
        if (!this.data?.length) {
            GlobalNotification.warning(i18n.t('general_text_no_data'));
            return;
        }
        const lastLabel = pageType === 'CLOUD'
            ? i18n.t('business_analysis_page_operator_person')
            : i18n.t('business_analysis_page_refund_operating_account');
        const header: string[][] = [[
            i18n.t('business_analysis_page_dish_name_one'),
            i18n.t('business_analysis_page_order_head_id'),
            i18n.t('business_analysis_page_order_account'),
            i18n.t('business_analysis_page_table_name'),
            i18n.t('general_text_price'),
            i18n.t('business_analysis_page_refund_quantity'),
            i18n.t('business_analysis_page_refund_time'),
            i18n.t('business_analysis_page_refund_reason'),
            lastLabel,
        ]];
        /** 导出列与表格一致：订单 ID + 订单号（check_id） */
        const rows = this.data.map((item) => {
            const r = item as RefundDishRow;
            return [
            String(r?.item_name1 ?? ''),
            formatRefundOrderHeadId(r),
            formatRefundCheckId(r),
            String(r?.table_name ?? ''),
            formatEUR(+(r?.product_price || 0)),
            String(r?.quantity ?? ''),
            r?.return_time ? moment(r.return_time).format('YYYY-MM-DD HH:mm:ss') : '',
            getRefundDishReasonDisplayText(r as RefundDishRow),
            String(r?.order_employee_id ?? ''),
            ];
        });
        const wpx = Array(9).fill({ wpx: 110 });
        exportExcel(
            header.concat(rows),
            `${i18n.t('business_analysis_page_refund_info')}.xlsx`,
            wpx
        );
    }
}

export default RefundDataStore

// 页面类型
export type PageType = 'CLOUD' | 'INSTANT'